// Universal-codebase: один и тот же UI работает на web и native через
// Capacitor.isNativePlatform(). Sprint 4 P-1 добавил pickFile/takePhoto/pickImage.
// Source of truth: docs/specs/sprint-4/stage-4-frontend.md §3.1 + stage-1-contracts.md §1.4.

import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Filesystem } from '@capacitor/filesystem';

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function platform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

export function deviceName(): string {
  switch (platform()) {
    case 'ios':
      return 'iOS app';
    case 'android':
      return 'Android app';
    case 'web':
    default:
      return `Web (${navigator.userAgent.slice(0, 80)})`;
  }
}

export type PickedFile = { blob: Blob; name: string; mimeType: string };

/**
 * Ссылка на выбранный файл БЕЗ содержимого.
 *
 * HEAL-46a D-3: содержимое читается по одному файлу, непосредственно перед
 * отправкой (см. readPicked). Раньше picker звали с readData:true, и он отдавал
 * весь файл в base64 — плюс 37% к размеру, и всё это жило в памяти WebView.
 * На одном файле терпимо; на пакете из десяти по 10 МБ это ~137 МБ и падение
 * процесса на бюджетном Android — не ошибка, а вылет приложения.
 */
export type PickedRef = { path?: string; name: string; mimeType: string; size: number; blob?: Blob };

/** Открывает системный picker файлов (PDF/JPG/PNG/HEIC). */
export async function pickFile(): Promise<PickedFile | null> {
  if (isNative()) {
    const result = await FilePicker.pickFiles({
      types: ['application/pdf', 'image/*'],
      readData: true,
      limit: 1,
    });
    if (!result.files.length) return null;
    const f = result.files[0];
    const bytes = base64ToBytes(f.data!);
    return { blob: new Blob([bytes], { type: f.mimeType }), name: f.name, mimeType: f.mimeType };
  }
  return await webInputFile({ accept: 'application/pdf,image/*' });
}

/**
 * Несколько файлов за один заход — документы и фотографии.
 *
 * Возвращает ССЫЛКИ, не содержимое (D-3, инвариант I-3). Содержимое берётся
 * позже, по одному, через readPicked.
 */
export async function pickFiles(limit: number): Promise<PickedRef[]> {
  if (isNative()) {
    const result = await FilePicker.pickFiles({
      types: ['application/pdf', 'image/*'],
      readData: false,
      // ⚠️ НЕ `limit` — именно 0, и это не небрежность.
      //
      // Плагин включает мультивыбор документов только при нуле, на обеих ОС:
      //   iOS     — `picker.allowsMultipleSelection = limit == 0`
      //   Android — `intent.putExtra(EXTRA_ALLOW_MULTIPLE, limit == 0)`
      // С `limit: 20` системный выбор отдаёт РОВНО ОДИН файл и молча, без
      // ошибки — на вебе этого не видно, потому что там свой путь через
      // <input multiple>. Найдено на живом iPhone 23.09: человек выбирал один
      // PDF, и загрузка сразу начиналась.
      //
      // Потолок в 20 файлов (D-11) держим сами, после выбора: лишние не
      // отбрасываются молча, а показываются на экране пакета с подписью
      // «Не поместился» (D-25). Плагину доверять счёт нельзя — он про него
      // ничего не знает, он только про «один или сколько угодно».
      limit: 0,
    });
    return result.files.map((f) => ({
      path: f.path,
      name: f.name,
      mimeType: f.mimeType,
      size: f.size,
    }));
  }
  return await webInputFiles({ accept: 'application/pdf,image/*', limit });
}

/**
 * Несколько фотографий из галереи.
 *
 * Через FilePicker, а НЕ через Camera.getPhoto: тот отдаёт ровно один кадр и
 * мультивыбора не поддерживает в принципе. Внутри на iOS — системный выбор
 * Apple (PHPickerViewController), который работает БЕЗ разрешения на медиатеку:
 * человек видит всю галерею и выбирает сам, приложение получает только
 * выбранное. Это закрывает и требования HEAL-68 (D-6).
 */
export async function pickImages(limit: number): Promise<PickedRef[]> {
  if (isNative()) {
    const result = await FilePicker.pickImages({
      readData: false,
      // ⚠️ Число можно просить только у iOS.
      //
      // iOS: PHPicker понимает `selectionLimit` буквально — 20 значит «до
      //      двадцати», и потолок лучше держать системным окном.
      // Android: мультивыбор включается ТОЛЬКО при нуле
      //      (`EXTRA_ALLOW_MULTIPLE = limit == 0`), ровно как у документов.
      //      С числом галерея отдаёт один снимок и молча.
      //
      // Чинить документы и не тронуть галерею было ошибкой: проверка шла на
      // iPhone, где число работает, и разница платформ прошла мимо (находка
      // ревью 24.09). Потолок в 20 держит `handleBatch` на своей стороне —
      // ему всё равно, сколько пришло.
      limit: platform() === 'ios' ? limit : 0,
    });
    return result.files.map((f) => ({
      path: f.path,
      name: f.name,
      mimeType: f.mimeType,
      size: f.size,
    }));
  }
  return await webInputFiles({ accept: 'image/*', limit });
}

/**
 * Прочитать содержимое ОДНОГО файла перед его отправкой.
 *
 * Вызывающий обязан освободить результат перед чтением следующего — на этом
 * держится инвариант «в памяти одновременно один файл».
 */
export async function readPicked(ref: PickedRef): Promise<PickedFile> {
  if (ref.blob) {
    return { blob: ref.blob, name: ref.name, mimeType: ref.mimeType };
  }

  // Через Filesystem, а не через picker: тот умеет отдавать содержимое только в
  // момент выбора (readData), то есть всё сразу — ровно то, чего мы избегаем.
  const result = await Filesystem.readFile({ path: ref.path! });
  const data = typeof result.data === 'string' ? result.data : await blobToBase64(result.data);
  const bytes = base64ToBytes(data);

  return { blob: new Blob([bytes], { type: ref.mimeType }), name: ref.name, mimeType: ref.mimeType };
}

/** Открывает камеру (native) или <input capture="environment"> (web). */
export async function takePhoto(): Promise<PickedFile | null> {
  if (isNative()) {
    const photo = await Camera.getPhoto({
      source: CameraSource.Camera,
      resultType: CameraResultType.Base64,
      quality: 85,
    });
    const bytes = base64ToBytes(photo.base64String!);
    const mimeType = `image/${photo.format}`;
    return { blob: new Blob([bytes], { type: mimeType }), name: `photo-${Date.now()}.${photo.format}`, mimeType };
  }
  return await webInputFile({ accept: 'image/*', capture: 'environment' });
}

/** Открывает gallery (native) или <input type="file" accept="image/*"> (web). */
export async function pickImage(): Promise<PickedFile | null> {
  if (isNative()) {
    const photo = await Camera.getPhoto({
      source: CameraSource.Photos,
      resultType: CameraResultType.Base64,
      quality: 85,
    });
    const bytes = base64ToBytes(photo.base64String!);
    const mimeType = `image/${photo.format}`;
    return { blob: new Blob([bytes], { type: mimeType }), name: `gallery-${Date.now()}.${photo.format}`, mimeType };
  }
  return await webInputFile({ accept: 'image/*' });
}

/** Filesystem.readFile на вебе отдаёт Blob вместо base64 — приводим к общему виду. */
async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);

  return btoa(binary);
}

function base64ToBytes(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return buffer;
}

function webInputFile(opts: { accept: string; capture?: 'environment' | 'user' }): Promise<PickedFile | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = opts.accept;
    if (opts.capture) input.setAttribute('capture', opts.capture);
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return resolve(null);
      resolve({ blob: f, name: f.name, mimeType: f.type });
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}

/**
 * Веб-ветка мультивыбора.
 *
 * На вебе содержимое и так живёт в File-объекте, читать его отдельно незачем —
 * поэтому ссылка сразу несёт blob. Экономия памяти нужна нативной оболочке, где
 * альтернатива — base64 всего пакета разом.
 */
function webInputFiles(opts: { accept: string; limit: number }): Promise<PickedRef[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = opts.accept;
    input.multiple = true;
    input.onchange = () => {
      const files = Array.from(input.files ?? []).slice(0, opts.limit);
      resolve(
        files.map((f) => ({ name: f.name, mimeType: f.type, size: f.size, blob: f })),
      );
    };
    input.oncancel = () => resolve([]);
    input.click();
  });
}
